import React, { useContext, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from '../../styles/pages/TelegramNotifications/TelegramNotificationsPage.module.css';
import { Title } from '../../components/UI/Custom/Title';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { PermissionDenied } from '../../components/UI/Custom/PermissionDenied';
import { ConfirmationModal } from '../../components/UI/Custom/ConfirmationModal';
import { Notification as Toast } from '../../components/UI/Notifications';
import { TelegramDestinationModal } from '../../components/Modal/TelegramNotifications/TelegramDestinationModal';
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';
import { getMyAccountApi } from '../../utils/api/Account/getMyAccountApi';
import { canManageTelegramNotifications } from '../../utils/custom/telegramNotificationsAccess';
import { getTelegramDestinationsApi } from '../../utils/api/Notification/getTelegramDestinationsApi';
import { createTelegramDestinationApi } from '../../utils/api/Notification/createTelegramDestinationApi';
import { updateTelegramDestinationApi } from '../../utils/api/Notification/updateTelegramDestinationApi';
import { deleteTelegramDestinationApi } from '../../utils/api/Notification/deleteTelegramDestinationApi';
import { sendTelegramTestApi } from '../../utils/api/Notification/sendTelegramTestApi';
import { createTelegramConnectTokenApi } from '../../utils/api/Notification/createTelegramConnectTokenApi';

const translations = {
    ru: {
        title: 'Telegram уведомления',
        accessDenied: 'Доступ к настройкам Telegram уведомлений отсутствует',
        add: 'Добавить',
        refresh: 'Обновить список',
        name: 'Название',
        chatId: 'Chat ID',
        routing: 'Маршрут',
        actions: 'События',
        status: 'Статус',
        controls: 'Действия',
        allActions: 'Все события',
        emptyList: 'Telegram направления пока не добавлены.',
        enabled: 'Включено',
        disabled: 'Выключено',
        global: 'Глобальный',
        manager: 'Менеджер',
        desk: 'Деск',
        previous: 'Пред.',
        next: 'След.',
        page: 'Страница',
        of: 'из',
        total: 'Всего',
        edit: 'Изменить',
        delete: 'Удалить',
        test: 'Тест',
        toggle: 'Вкл/выкл',
        testSuccess: 'Тестовое сообщение отправлено.',
        toggleSuccess: 'Статус Telegram направления обновлен.',
        deleteSuccess: 'Telegram направление удалено.',
        createSuccess: 'Telegram направление создано.',
        updateSuccess: 'Telegram направление обновлено.',
        connectSuccess: 'Connect token сгенерирован. Завершите подключение в Telegram группе.',
        deleteTitle: 'Удаление Telegram направления',
        deleteMessage: 'Удалить Telegram направление "{name}"?',
        deleteConfirm: 'Удалить',
        cancel: 'Отмена',
        connectTitle: 'Подключение Telegram группы',
        connectDescription: 'Сгенерируйте одноразовый connect token, добавьте бота в группу и отправьте команду в этой группе. Chat ID подтянется автоматически.',
        connectGenerate: 'Сгенерировать token',
        connectRegenerate: 'Сгенерировать новый token',
        connectBotLabel: 'Бот',
        connectBotFallback: 'Укажите TELEGRAM_BOT_USERNAME в API env, чтобы CRM показывала ссылку на бота.',
        connectCommandLabel: 'Команда для группы',
        connectExpiresLabel: 'Действует до',
        connectStepsTitle: 'Шаги',
        connectStep1: 'Добавьте бота в нужную Telegram группу.',
        connectStep2: 'Отправьте команду ниже в этой группе от имени super admin.',
        connectStep3: 'После ответа бота вернитесь сюда и обновите список направлений.',
    },
    en: {
        title: 'Telegram Notifications',
        accessDenied: 'Access to Telegram notifications settings is denied',
        add: 'Add',
        refresh: 'Refresh list',
        name: 'Name',
        chatId: 'Chat ID',
        routing: 'Routing',
        actions: 'Actions',
        status: 'Status',
        controls: 'Controls',
        allActions: 'All actions',
        emptyList: 'No Telegram destinations yet.',
        enabled: 'Enabled',
        disabled: 'Disabled',
        global: 'Global',
        manager: 'Manager',
        desk: 'Desk',
        previous: 'Prev.',
        next: 'Next',
        page: 'Page',
        of: 'of',
        total: 'Total',
        edit: 'Edit',
        delete: 'Delete',
        test: 'Test',
        toggle: 'Toggle',
        testSuccess: 'Test message sent.',
        toggleSuccess: 'Telegram destination status updated.',
        deleteSuccess: 'Telegram destination deleted.',
        createSuccess: 'Telegram destination created.',
        updateSuccess: 'Telegram destination updated.',
        connectSuccess: 'Connect token generated. Finish the connection in the Telegram group.',
        deleteTitle: 'Delete Telegram Destination',
        deleteMessage: 'Delete Telegram destination "{name}"?',
        deleteConfirm: 'Delete',
        cancel: 'Cancel',
        connectTitle: 'Connect Telegram Group',
        connectDescription: 'Generate a one-time connect token, add the bot to a group, and send the command from that group. The chat ID will be captured automatically.',
        connectGenerate: 'Generate token',
        connectRegenerate: 'Generate new token',
        connectBotLabel: 'Bot',
        connectBotFallback: 'Set TELEGRAM_BOT_USERNAME in the API env so CRM can show the bot link.',
        connectCommandLabel: 'Group command',
        connectExpiresLabel: 'Expires at',
        connectStepsTitle: 'Steps',
        connectStep1: 'Add the bot to the target Telegram group.',
        connectStep2: 'Send the command below in that group as a super admin.',
        connectStep3: 'After the bot replies, return here and refresh the destinations list.',
    },
};

const buildUpdatePayload = (destination, overrides = {}) => ({
    name: destination.name,
    chat_id: destination.chat_id,
    is_enabled: destination.is_enabled,
    manager_id: destination.manager_id,
    desk_id: destination.desk_id,
    actions: destination.actions || [],
    ...overrides,
});

export const TelegramNotificationsPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';

    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [destinations, setDestinations] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, per_page: 15, total: 0 });
    const [toast, setToast] = useState(null);
    const [destinationToDelete, setDestinationToDelete] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDestination, setEditingDestination] = useState(null);
    const [isSubmittingModal, setIsSubmittingModal] = useState(false);
    const [connectTokenData, setConnectTokenData] = useState(null);
    const [isGeneratingConnectToken, setIsGeneratingConnectToken] = useState(false);

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.per_page || 15)));
    }, [pagination]);

    const formatDateTime = (value) => {
        if (!value) {
            return '—';
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString(langSelected === 'ru' ? 'uk-UA' : 'en-US');
    };

    const loadDestinations = async (page = pagination.page, perPage = pagination.per_page) => {
        setIsLoading(true);

        try {
            const data = await getTelegramDestinationsApi(page, perPage);
            const items = Array.isArray(data.items) ? data.items : [];
            setDestinations(items);
            setPagination({
                page: data.page || page,
                per_page: data.per_page || perPage,
                total: data.total || 0,
            });
        } catch (error) {
            console.error('Failed to load Telegram destinations:', error);
            setDestinations([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const bootstrap = async () => {
            setIsLoading(true);

            try {
                const accountData = await getMyAccountApi();
                const access = canManageTelegramNotifications(accountData);
                setHasAccess(access);

                if (access) {
                    await loadDestinations(1, pagination.per_page);
                    return;
                }
            } catch (error) {
                console.error('Failed to load Telegram notifications access:', error);
                setHasAccess(false);
            }

            setIsLoading(false);
        };

        bootstrap();
    }, []);

    const formatRouting = (destination) => {
        if (destination.manager_id) {
            return t.manager + ' #' + destination.manager_id;
        }

        if (destination.desk_id) {
            return t.desk + ' #' + destination.desk_id;
        }

        return t.global;
    };

    const formatActions = (destination) => {
        if (!Array.isArray(destination.actions) || destination.actions.length === 0) {
            return t.allActions;
        }

        return destination.actions.join(', ');
    };

    const handleOpenCreateModal = () => {
        setEditingDestination(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (destination) => {
        setEditingDestination(destination);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        if (isSubmittingModal) {
            return;
        }

        setIsModalOpen(false);
        setEditingDestination(null);
    };

    const handleSaveDestination = async (payload, id = null) => {
        setIsSubmittingModal(true);

        try {
            if (id) {
                await updateTelegramDestinationApi(id, payload);
                setToast({ message: t.updateSuccess, type: 'info' });
            } else {
                await createTelegramDestinationApi(payload);
                setToast({ message: t.createSuccess, type: 'info' });
            }

            setIsModalOpen(false);
            setEditingDestination(null);
            await loadDestinations(pagination.page, pagination.per_page);
        } catch (error) {
            console.error('Failed to save Telegram destination:', error);
        } finally {
            setIsSubmittingModal(false);
        }
    };

    const handleToggle = async (destination) => {
        try {
            await updateTelegramDestinationApi(
                destination.id,
                buildUpdatePayload(destination, {
                    is_enabled: !destination.is_enabled,
                })
            );

            setToast({ message: t.toggleSuccess, type: 'info' });
            await loadDestinations(pagination.page, pagination.per_page);
        } catch (error) {
            console.error('Failed to update Telegram destination:', error);
        }
    };

    const handleTest = async (chatId, message = '') => {
        try {
            await sendTelegramTestApi(chatId, message);
            setToast({ message: t.testSuccess, type: 'info' });
        } catch (error) {
            console.error('Failed to send Telegram test message:', error);
        }
    };

    const handleGenerateConnectToken = async () => {
        setIsGeneratingConnectToken(true);

        try {
            const data = await createTelegramConnectTokenApi();
            setConnectTokenData(data);
            setToast({ message: t.connectSuccess, type: 'info' });
        } catch (error) {
            console.error('Failed to generate Telegram connect token:', error);
        } finally {
            setIsGeneratingConnectToken(false);
        }
    };

    const handleDelete = async () => {
        if (!destinationToDelete) {
            return;
        }

        try {
            await deleteTelegramDestinationApi(destinationToDelete.id);
            setToast({ message: t.deleteSuccess, type: 'info' });
            setDestinationToDelete(null);

            const nextPage = destinations.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
            await loadDestinations(nextPage, pagination.per_page);
        } catch (error) {
            console.error('Failed to delete Telegram destination:', error);
        }
    };

    const handlePreviousPage = () => {
        if (pagination.page <= 1) {
            return;
        }

        loadDestinations(pagination.page - 1, pagination.per_page);
    };

    const handleNextPage = () => {
        if (pagination.page >= totalPages) {
            return;
        }

        loadDestinations(pagination.page + 1, pagination.per_page);
    };

    if (isLoading) {
        return (
            <div className={styles.loadingBlock + (isDark ? ' ' + styles.darkLoadingBlock : '')}>
                <LoadingCircle />
            </div>
        );
    }

    if (!hasAccess) {
        return <PermissionDenied text={t.accessDenied} />;
    }

    return (
        <div className={styles.container}>
            {toast && <Toast message={toast.message} type={toast.type} />}

            <Title h2={t.title} activeSpan={true} />

            <div className={styles.content + (isDark ? ' ' + styles.darkContent : '')}>
                <div className={styles.topBar}>
                    <div className={styles.summary}>
                        <span>{t.total}: {pagination.total}</span>
                        <span>{t.page} {pagination.page} {t.of} {totalPages}</span>
                    </div>

                    <div className={styles.topBarActions}>
                        <button onClick={() => loadDestinations(pagination.page, pagination.per_page)} className={styles.secondaryButton}>
                            {t.refresh}
                        </button>
                        <button onClick={handleOpenCreateModal} className={styles.addButton + (isDark ? ' ' + styles.darkAddButton : '')}>
                            <AddIcon style={{ fontSize: '1.1rem' }} />
                            <span>{t.add}</span>
                        </button>
                    </div>
                </div>

                <div className={styles.connectCard + (isDark ? ' ' + styles.darkConnectCard : '')}>
                    <div className={styles.connectHeader}>
                        <div>
                            <h3 className={styles.connectTitle + (isDark ? ' ' + styles.darkConnectTitle : '')}>{t.connectTitle}</h3>
                            <p className={styles.connectDescription + (isDark ? ' ' + styles.darkConnectDescription : '')}>{t.connectDescription}</p>
                        </div>
                        <button
                            onClick={handleGenerateConnectToken}
                            disabled={isGeneratingConnectToken}
                            className={styles.addButton + (isDark ? ' ' + styles.darkAddButton : '')}
                        >
                            <AddIcon style={{ fontSize: '1.1rem' }} />
                            <span>{connectTokenData ? t.connectRegenerate : t.connectGenerate}</span>
                        </button>
                    </div>

                    <div className={styles.connectSteps + (isDark ? ' ' + styles.darkConnectSteps : '')}>
                        <div className={styles.connectStepsTitle}>{t.connectStepsTitle}</div>
                        <ol className={styles.connectStepsList}>
                            <li>{t.connectStep1}</li>
                            <li>{t.connectStep2}</li>
                            <li>{t.connectStep3}</li>
                        </ol>
                    </div>

                    {connectTokenData && (
                        <div className={styles.connectDetails}>
                            <div className={styles.connectField}>
                                <span className={styles.connectLabel + (isDark ? ' ' + styles.darkConnectLabel : '')}>{t.connectBotLabel}</span>
                                {connectTokenData.bot_link ? (
                                    <a
                                        href={connectTokenData.bot_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={styles.connectLink}
                                    >
                                        @{String(connectTokenData.bot_username || '').replace(/^@/, '')}
                                    </a>
                                ) : (
                                    <span className={styles.connectFallback + (isDark ? ' ' + styles.darkConnectFallback : '')}>{t.connectBotFallback}</span>
                                )}
                            </div>

                            <div className={styles.connectField}>
                                <span className={styles.connectLabel + (isDark ? ' ' + styles.darkConnectLabel : '')}>{t.connectCommandLabel}</span>
                                <code className={styles.connectCode + (isDark ? ' ' + styles.darkConnectCode : '')}>{connectTokenData.command}</code>
                            </div>

                            <div className={styles.connectField}>
                                <span className={styles.connectLabel + (isDark ? ' ' + styles.darkConnectLabel : '')}>{t.connectExpiresLabel}</span>
                                <span className={styles.connectExpires + (isDark ? ' ' + styles.darkConnectExpires : '')}>{formatDateTime(connectTokenData.expires_at)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={styles.tableHeader + (isDark ? ' ' + styles.darkTableHeader : '')}>
                            <tr>
                                <th>{t.name}</th>
                                <th>{t.chatId}</th>
                                <th>{t.routing}</th>
                                <th>{t.actions}</th>
                                <th>{t.status}</th>
                                <th>{t.controls}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {destinations.length > 0 ? (
                                destinations.map((destination) => (
                                    <tr key={destination.id} className={styles.tableRow + (isDark ? ' ' + styles.darkTableRow : '')}>
                                        <td className={styles.tableCell + (isDark ? ' ' + styles.darkTableCell : '')}>{destination.name}</td>
                                        <td className={styles.tableCell + (isDark ? ' ' + styles.darkTableCell : '')}>{destination.chat_id}</td>
                                        <td className={styles.tableCell + (isDark ? ' ' + styles.darkTableCell : '')}>{formatRouting(destination)}</td>
                                        <td className={styles.tableCell + (isDark ? ' ' + styles.darkTableCell : '')}>{formatActions(destination)}</td>
                                        <td className={styles.tableCell + (isDark ? ' ' + styles.darkTableCell : '')}>
                                            <span className={styles.statusBadge + ' ' + (destination.is_enabled ? styles.enabledBadge : styles.disabledBadge)}>
                                                {destination.is_enabled ? t.enabled : t.disabled}
                                            </span>
                                        </td>
                                        <td className={styles.tableCell + ' ' + styles.actionsCell + (isDark ? ' ' + styles.darkTableCell : '')}>
                                            <button onClick={() => handleOpenEditModal(destination)} className={styles.secondaryButton} title={t.edit}>
                                                <EditIcon style={{ fontSize: '1rem' }} />
                                                {t.edit}
                                            </button>
                                            <button onClick={() => handleToggle(destination)} className={styles.secondaryButton} title={t.toggle}>
                                                <PowerSettingsNewIcon style={{ fontSize: '1rem' }} />
                                                {t.toggle}
                                            </button>
                                            <button onClick={() => handleTest(destination.chat_id)} className={styles.secondaryButton} title={t.test}>
                                                <SendIcon style={{ fontSize: '1rem' }} />
                                                {t.test}
                                            </button>
                                            <button onClick={() => setDestinationToDelete(destination)} className={styles.deleteButton} title={t.delete}>
                                                <DeleteIcon style={{ fontSize: '1rem' }} />
                                                {t.delete}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className={styles.emptyState + (isDark ? ' ' + styles.darkEmptyState : '')}>
                                        {t.emptyList}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination.total > pagination.per_page && (
                    <div className={styles.pagination + (isDark ? ' ' + styles.darkPagination : '')}>
                        <button onClick={handlePreviousPage} disabled={pagination.page <= 1} className={styles.paginationButton}>
                            <ChevronLeftIcon style={{ fontSize: '1rem' }} />
                            {t.previous}
                        </button>
                        <span>{t.page} {pagination.page} {t.of} {totalPages}</span>
                        <button onClick={handleNextPage} disabled={pagination.page >= totalPages} className={styles.paginationButton}>
                            {t.next}
                            <ChevronRightIcon style={{ fontSize: '1rem' }} />
                        </button>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={Boolean(destinationToDelete)}
                onClose={() => setDestinationToDelete(null)}
                onConfirm={handleDelete}
                title={t.deleteTitle}
                message={t.deleteMessage.replace('{name}', destinationToDelete?.name || '')}
                confirmText={t.deleteConfirm}
                cancelText={t.cancel}
            />

            <TelegramDestinationModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveDestination}
                initialData={editingDestination}
                isSubmitting={isSubmittingModal}
            />
        </div>
    );
};
